/**
 * The navbar search's brain: turns the /search/index payload into
 * searchable records, and ranks them against what the user typed.
 *
 * Everything here is pure and synchronous. The index is ~1,200 records
 * and matching one query over all of them takes a millisecond or two,
 * so there is no worker, no debounce and no request per keystroke.
 *
 * Arabic is normalised before anything is compared, so the user never
 * has to type the exact spelling stored in the database:
 *   أ إ آ ٱ → ا    ة → ه    ى ئ → ي    ؤ → و
 *   tashkeel and tatweel dropped    ٠-٩ / ۰-۹ → 0-9
 * and a word matches with or without its «ال» («حلبي» finds «الحلبي»),
 * with or without a space («عبدالله» finds «عبد الله»).
 */

import { guidesFor, ASK_WORDS } from '../help/guides';

const LETTERS = {
  'أ': 'ا', 'إ': 'ا', 'آ': 'ا', 'ٱ': 'ا', 'ة': 'ه', 'ى': 'ي', 'ئ': 'ي', 'ؤ': 'و',
};
// tashkeel, superscript alef, tatweel
const DROP = /[ً-ٰٟـ]/;
// Anything that only separates words in a name or a label:
// "الصف ثامن - أولى م (ص)" must read as the words themselves.
const SEPARATOR = /[\s\-_(),.،؛:;/\\|'"«»]/;

function normChar(c) {
  const code = c.charCodeAt(0);
  if (code >= 0x660 && code <= 0x669) return String(code - 0x660);
  if (code >= 0x6f0 && code <= 0x6f9) return String(code - 0x6f0);
  return LETTERS[c] || c.toLowerCase();
}

/**
 * Normalised text plus, for every character of it, the index of the
 * original character it came from — what lets a match found in the
 * normalised text be highlighted in the text as the user sees it.
 */
function normalizeWithMap(str) {
  let text = '';
  const map = [];
  const s = String(str ?? '');
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    if (DROP.test(c)) continue;
    if (SEPARATOR.test(c)) {
      if (text && text[text.length - 1] !== ' ') { text += ' '; map.push(i); }
      continue;
    }
    text += normChar(c);
    map.push(i);
  }
  if (text.endsWith(' ')) { text = text.slice(0, -1); map.pop(); }
  return { text, map };
}

export const normalize = (str) => normalizeWithMap(str).text;

/** Latin + Arabic-Indic digits only, e.g. "+90 536 ٠٢٥" → "90536025". */
const digitsOf = (str) => normalize(str).replace(/\D/g, '');

/* ------------------------------------------------------------------ */
/* Building the index                                                  */
/* ------------------------------------------------------------------ */

function field(label, value, weight, { phone = false } = {}) {
  if (value === null || value === undefined || value === '') return null;
  const text = normalize(value);
  return {
    label,
    value: String(value),
    weight,
    phone,
    // Padded with spaces so "starts a word" is one includes(' ' + tok).
    hay: ` ${text}${joined(text)} `,
    compact: text.replace(/ /g, ''),
    digits: phone ? digitsOf(value) : null,
  };
}

const compactFields = (list) => list.filter(Boolean);

// Compound names are written both ways — «عبد الله» and «عبدالله»,
// «أبو بكر» and «أبوبكر». The joined spelling is appended to the field's
// text, so either one typed matches as a whole word.
const COMPOUND = /(^| )(عبد|ابو) (?=\S)/g;
function joined(text) {
  const j = text.replace(COMPOUND, '$1$2');
  return j === text ? '' : ` ${j}`;
}

/**
 * Type metadata: the label a group is shown under, and where a chosen
 * result lands (the breadcrumb that tells the user what Enter will do
 * BEFORE they press it).
 */
export const TYPES = {
  page: { group: 'الصفحات', order: 0 },
  student: { group: 'الطلاب', order: 1 },
  section: { group: 'الشعب', order: 2 },
  teacher: { group: 'المعلمون', order: 3 },
  driver: { group: 'السائقون والباصات', order: 4 },
  family: { group: 'العائلات', order: 5 },
  guide: { group: 'كيف أعمل', order: 6 },
};

// Extra words a page answers to, beyond its sidebar label — people
// search for what they want to DO, not for the menu item's name.
const PAGE_KEYWORDS = {
  'teacher-home': 'رئيسية صفي شعبي تفقد حضور',
  monitor: 'مراقبة احصائيات حالة المدرسة مباشر',
  'student-directory': 'بحث ملف طالب هواتف اتصال',
  students: 'تسجيل طالب جديد اضافة',
  families: 'اهل اولياء امور',
  enrollments: 'قيد نقل شعبه تسجيل',
  attendance: 'حضور غياب تفقد تاخير دوام',
  exams: 'علامات درجات نتائج امتحان مذاكره',
  teachers: 'استاذ اساتذه مدرس',
  assignments: 'مواد تدريس توزيع',
  sections: 'شعب صفوف',
  grades: 'صف',
  subjects: 'ماده',
  batches: 'فوج دوام صباحي مسائي',
  transportation: 'باص سائق نقل مواصلات',
  payments: 'قسط دفع رسوم فلوس مال وصل ايصال',
  users: 'حسابات دخول مستخدم رمز',
  notifications: 'رسائل تنبيه ارسال',
  help: 'مساعده دليل شرح كيف تعليمات طريقه',
};

/**
 * @param {object} data   the /search/index payload
 * @param {Array}  nav    the role-filtered page list from App.js
 * @returns {Array} records, each { key, type, id, title, subtitle,
 *          inactive, fields, actions }
 *
 * `actions[0]` is what Enter / a click does; the rest are shortcuts
 * shown on the highlighted row. An action is only offered when the
 * user's role can open its page — `nav` is already filtered by role.
 */
export function buildIndex(data, nav) {
  const can = new Set(nav.map((p) => p.id));
  const records = [];
  const act = (page, label, intent = {}) => (can.has(page) ? { page, label, intent } : null);
  const push = (rec) => {
    rec.actions = rec.actions.filter(Boolean);
    if (rec.actions.length) records.push(rec);
  };

  nav.forEach((p) => {
    push({
      key: `page:${p.id}`, type: 'page', id: p.id, title: p.label,
      subtitle: `الانتقال إلى صفحة ${p.label}`,
      icon: p.icon,
      fields: compactFields([field('الصفحة', p.label, 1), field('', PAGE_KEYWORDS[p.id], 0.6)]),
      actions: [act(p.id, 'فتح الصفحة')],
    });
  });

  // شؤون الطلاب's two office views, found by what people call them
  // («اضبارة», «تسجيل طالب»). `students` is office-only, so it tells the
  // office apart from a teacher, who has شؤون الطلاب too.
  if (can.has('student-directory') && can.has('students')) {
    const icon = nav.find((p) => p.id === 'student-directory')?.icon;
    [
      { view: 'new', title: 'إضبارة جديدة', subtitle: 'شؤون الطلاب ← فتح إضبارة طالب جديد', words: 'اضافه طالب جديد تسجيل فتح اضباره استماره' },
      { view: 'dossiers', title: 'الإضبارات', subtitle: 'شؤون الطلاب ← الإضبارات المفتوحة مؤخراً', words: 'اضبارات اضباراتي اليوم امس الاسبوع المسجلين الجدد' },
    ].forEach((v) => push({
      key: `page:student-directory:${v.view}`, type: 'page', id: 'student-directory', title: v.title,
      subtitle: v.subtitle,
      icon,
      fields: compactFields([field('الصفحة', v.title, 1), field('', v.words, 0.6)]),
      actions: [act('student-directory', 'فتح الصفحة', { view: v.view })],
    }));
  }

  // «دليل الاستخدام»: what people ask how to do («كيف أضيف شعبة»).
  // Enter walks them through it (ورّيني); the second action reads it.
  guidesFor(null, nav).forEach((x) => push({
    key: `guide:${x.id}`, type: 'guide', id: x.id, title: x.title,
    subtitle: x.summary,
    icon: x.icon,
    fields: compactFields([
      field('الدليل', x.title, 1),
      field('', `${x.words} ${ASK_WORDS}`, 0.7),
    ]),
    actions: [
      act('help', 'ورّيني', { guide: x.id, run: true }),
      act('help', 'الخطوات مكتوبة', { guide: x.id }),
    ],
  }));

  if (!data) return records;

  (data.students || []).forEach((s) => {
    push({
      key: `student:${s.id}`, type: 'student', id: s.id, title: s.name,
      subtitle: s.section_label || 'بلا شعبة',
      inactive: s.status !== 'active',
      fields: compactFields([
        field('الاسم', s.name, 1),
        field('اسم الأب', s.father_name, 0.55),
        field('اسم الأم', s.mother_name, 0.5),
        field('الشعبة', s.section_label, 0.35),
        field('هاتف الأب', s.father_phone, 0.9, { phone: true }),
        field('هاتف الأم', s.mother_phone, 0.9, { phone: true }),
        field('هاتف الطالب', s.student_phone, 0.9, { phone: true }),
        field('الرقم الوطني', s.national_id, 0.95, { phone: true }),
        field('رقم التسجيل', s.registration_number, 0.95, { phone: true }),
        field('رقم الباص', s.bus_number, 0.4),
      ]),
      actions: [
        act('student-directory', 'ملف الطالب', { studentId: s.id }),
        act('student-directory', 'الحضور', { studentId: s.id, tab: 'attendance' }),
        act('student-directory', 'العلامات', { studentId: s.id, tab: 'exams' }),
        // Fees are office business: only offered to whoever can open الأقساط.
        can.has('payments') ? act('student-directory', 'الأقساط', { studentId: s.id, tab: 'payments' }) : null,
      ],
    });
  });

  (data.sections || []).forEach((s) => {
    const n = s.student_count || 0;
    push({
      key: `section:${s.id}`, type: 'section', id: s.id, title: s.section_label,
      subtitle: `${n} طالب${s.homeroom_teacher_name ? ` • معلم الصف: ${s.homeroom_teacher_name}` : ''}`,
      fields: compactFields([
        field('الشعبة', s.section_label, 1),
        field('معلم الصف', s.homeroom_teacher_name, 0.45),
      ]),
      actions: [
        act('student-directory', 'طلاب الشعبة', { sectionId: s.id }),
        act('attendance', 'التفقّد', { sectionId: s.id }),
        act('sections', 'تعديل الشعبة', { sectionId: s.id }),
      ],
    });
  });

  (data.teachers || []).forEach((t) => {
    push({
      key: `teacher:${t.id}`, type: 'teacher', id: t.id, title: t.name,
      subtitle: t.subjects || 'بلا مواد مسندة',
      inactive: t.status !== 'active',
      fields: compactFields([
        field('الاسم', t.name, 1),
        field('الهاتف', t.phone, 0.9, { phone: true }),
        field('المواد', t.subjects, 0.4),
      ]),
      actions: [
        act('teachers', 'بيانات المعلم', { teacherId: t.id }),
        act('assignments', 'تدريس المواد', {}),
      ],
    });
  });

  (data.drivers || []).forEach((d) => {
    push({
      key: `driver:${d.id}`, type: 'driver', id: d.id, title: d.driver_name,
      subtitle: `باص ${d.bus_number} • ${d.student_count || 0} طالب`,
      inactive: d.status !== 'active',
      fields: compactFields([
        field('السائق', d.driver_name, 1),
        field('رقم الباص', d.bus_number, 0.95),
        field('رقم الهوية', d.id_number, 0.9, { phone: true }),
        field('المركبة', d.vehicle_details, 0.4),
      ]),
      actions: [
        act('transportation', 'بيانات السائق', { driverId: d.id }),
        act('student-directory', 'طلاب الباص', { query: d.bus_number }),
      ],
    });
  });

  (data.families || []).forEach((f) => {
    const head = [f.father_name, f.last_name].filter(Boolean).join(' ')
      || f.mother_name || 'عائلة بلا اسم';
    push({
      key: `family:${f.id}`, type: 'family', id: f.id, title: `عائلة ${head}`,
      subtitle: f.student_names ? `الأبناء: ${f.student_names}` : 'لا أبناء مسجّلون',
      fields: compactFields([
        field('اسم الأب', f.father_name, 0.9),
        field('الكنية', f.last_name, 0.8),
        field('اسم الأم', f.mother_name, 0.7),
        field('هاتف الأب', f.father_phone, 0.9, { phone: true }),
        field('هاتف الأم', f.mother_phone, 0.9, { phone: true }),
        field('الأبناء', f.student_names, 0.45),
      ]),
      actions: [
        act('families', 'أفراد العائلة', { familyId: f.id }),
        act('families', 'تعديل العائلة', { familyId: f.id, edit: true }),
      ],
    });
  });

  return records;
}

/* ------------------------------------------------------------------ */
/* Querying                                                            */
/* ------------------------------------------------------------------ */

/**
 * A query that is all digits (spaces, + and dashes allowed) is ONE
 * number — "0536 025 94 33" is a phone, not four search words. A local
 * number's leading 0 is dropped so it still finds the stored
 * international form (+90536…, +963…).
 */
export function parseQuery(raw) {
  const text = normalize(raw);
  if (!text) return { text: '', tokens: [] };
  // Separators are already spaces after normalize().
  if (/^\+?[\d ]+$/.test(text) && /\d{3}/.test(text.replace(/ /g, ''))) {
    const digits = text.replace(/\D/g, '');
    return { text, tokens: [{ text: digits, digits: digits.replace(/^0+/, '') || digits }] };
  }
  return {
    text,
    tokens: text.split(' ').filter(Boolean).map((t) => ({
      text: t,
      digits: /^\d{3,}$/.test(t) ? t.replace(/^0+/, '') || t : null,
    })),
  };
}

/** How well one token matches one field, 0..100 (before weighting). */
function matchField(f, tok) {
  if (tok.digits && f.digits) {
    if (f.digits === tok.digits) return 100;
    if (f.digits.endsWith(tok.digits)) return 90;
    if (f.digits.includes(tok.digits)) return 75;
  }
  const t = tok.text;
  if (f.hay.includes(` ${t} `)) return 100;           // whole word
  if (f.hay.includes(` ${t}`)) return 82;             // start of a word
  if (t.length >= 2 && f.hay.includes(` ال${t}`)) return 78; // «حلبي» → «الحلبي»
  if (t.length >= 2 && f.hay.includes(t)) return 50;  // inside a word
  if (t.length >= 3 && f.compact.includes(t)) return 45; // «عبدالله» → «عبد الله»
  return 0;
}

function scoreRecord(rec, q) {
  let total = 0;
  const matched = new Set();
  for (const tok of q.tokens) {
    let best = 0;
    let bestField = null;
    for (const f of rec.fields) {
      const s = matchField(f, tok) * f.weight;
      if (s > best) { best = s; bestField = f; }
    }
    // Every word typed must match something: "أحمد ثامن" means Ahmad
    // IN grade eight, not everyone called Ahmad plus all of grade eight.
    if (!best) return null;
    total += best;
    matched.add(bestField);
  }
  const title = rec.fields[0];
  if (title && q.text.length > 1) {
    if (title.hay.startsWith(` ${q.text}`)) total += 70;
    else if (title.hay.includes(` ${q.text}`)) total += 30;
  }
  // Shorter titles win a tie: typing «أحمد» should rank «أحمد علي»
  // above «أحمد عبد الرحمن الشيخ محمد».
  total -= Math.min(title?.value.length || 0, 60) * 0.15;
  if (rec.inactive) total *= 0.55;
  // Why this result matched, when it was not by its title — the row
  // then shows e.g. «هاتف الأم: +90534…» so the user can see at a
  // glance that it is the right person.
  const why = [...matched].find((f) => f !== title && f.label && !rec.subtitle.includes(f.value)) || null;
  return { total, why };
}

const GROUP_LIMIT = {
  page: 3, student: 7, section: 4, teacher: 4, driver: 4, family: 4, guide: 3,
};

/**
 * @returns {Array<{ type, label, total, items: [{ rec, why, score }] }>}
 *   groups ordered by their best hit, so typing a class name puts
 *   الشعب first and a person's name puts الطلاب first.
 */
export function search(records, raw, { limit } = {}) {
  const q = typeof raw === 'string' ? parseQuery(raw) : raw;
  if (!q.tokens.length) return [];
  const byType = {};
  for (const rec of records) {
    const r = scoreRecord(rec, q);
    if (!r) continue;
    (byType[rec.type] = byType[rec.type] || []).push({ rec, why: r.why, score: r.total });
  }
  return Object.entries(byType)
    .map(([type, hits]) => {
      hits.sort((a, b) => b.score - a.score);
      return {
        type,
        label: TYPES[type].group,
        total: hits.length,
        best: hits[0].score,
        items: hits.slice(0, limit || GROUP_LIMIT[type] || 4),
      };
    })
    // Near-ties go to the more common kind: «يوسف» matches a teacher and
    // 25 pupils equally well, and the office is almost always after a
    // pupil. A clearly better match (a class name → الشعب) still leads.
    .sort((a, b) => (Math.abs(b.best - a.best) > 15 ? b.best - a.best : TYPES[a.type].order - TYPES[b.type].order));
}

/**
 * Split `original` into [{ text, hit }] segments, marking every place a
 * query token occurs — found on the normalised text, painted on the
 * original, so «احمد» lights up «أحمد» exactly.
 */
export function highlight(original, q, { phone = false } = {}) {
  const s = String(original ?? '');
  if (!q?.tokens?.length || !s) return [{ text: s, hit: false }];
  const ranges = [];

  if (phone) {
    // Match on the digits, map back through the non-digit characters.
    const idx = [];
    const digits = [];
    for (let i = 0; i < s.length; i += 1) {
      const d = normChar(s[i]);
      if (/\d/.test(d)) { digits.push(d); idx.push(i); }
    }
    const ds = digits.join('');
    q.tokens.forEach((tok) => {
      const t = tok.digits || tok.text;
      if (!t) return;
      const at = ds.lastIndexOf(t);
      if (at >= 0) ranges.push([idx[at], idx[at + t.length - 1] + 1]);
    });
  } else {
    const { text, map } = normalizeWithMap(s);
    q.tokens.forEach((tok) => {
      if (!tok.text) return;
      let from = 0;
      for (;;) {
        const at = text.indexOf(tok.text, from);
        if (at < 0) break;
        // A one-letter query only ever matched word starts; paint only those.
        if (tok.text.length === 1 && at > 0 && text[at - 1] !== ' ') { from = at + 1; continue; }
        ranges.push([map[at], map[at + tok.text.length - 1] + 1]);
        from = at + tok.text.length;
      }
    });
    // «عبدالله» typed, «عبد الله» shown: match again with the spaces
    // taken out, so the joined spelling still lights up.
    if (!ranges.length) {
      const cMap = [];
      let compact = '';
      for (let i = 0; i < text.length; i += 1) {
        if (text[i] !== ' ') { compact += text[i]; cMap.push(map[i]); }
      }
      q.tokens.forEach((tok) => {
        const at = tok.text.length >= 3 ? compact.indexOf(tok.text) : -1;
        if (at >= 0) ranges.push([cMap[at], cMap[at + tok.text.length - 1] + 1]);
      });
    }
    // Paint whole words. Wrapping part of an Arabic word in <mark> cuts
    // its letters apart («ال|حلبي» renders as two broken pieces), and the
    // whole word is what the eye looks for anyway.
    ranges.forEach((r) => {
      while (r[0] > 0 && !SEPARATOR.test(s[r[0] - 1])) r[0] -= 1;
      while (r[1] < s.length && !SEPARATOR.test(s[r[1]])) r[1] += 1;
    });
  }

  if (!ranges.length) return [{ text: s, hit: false }];
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  ranges.forEach(([a, b]) => {
    const last = merged[merged.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b);
    else merged.push([a, b]);
  });
  const out = [];
  let pos = 0;
  merged.forEach(([a, b]) => {
    if (a > pos) out.push({ text: s.slice(pos, a), hit: false });
    out.push({ text: s.slice(a, b), hit: true });
    pos = b;
  });
  if (pos < s.length) out.push({ text: s.slice(pos), hit: false });
  return out;
}
